import { Box, Typography } from "@mui/material";
import React from "react";
import { WorkflowListItem } from "../../../Services/WorkflowService";
import { BaseUrl, ChatBaseUrl, RedirectUrl } from "../../../Constant/Setting";

interface WorkflowPublicApiProps {
    workflow: WorkflowListItem;
}

const WorkflowPublicApi: React.FC<WorkflowPublicApiProps> = ({ workflow }) => {
    const configuredBase = BaseUrl || ChatBaseUrl || RedirectUrl || (typeof window !== 'undefined' ? window.location.origin : "");
    const baseUrl = configuredBase?.replace(/\/$/, "");
    const apiPrefix = baseUrl?.endsWith("/api") ? "" : "/api";
    const endpoint = `${baseUrl}${apiPrefix}/workflows/nodes/${workflow.id}/chat`;

    const curlExample = `curl -X POST \\
  '${endpoint}' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "user_input": "string"
  }'`;

    const fetchExample = `const BASE_URL = '${baseUrl}';
fetch(\`\$\{BASE_URL\}/api/workflows/nodes/${workflow.id}/chat\`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    user_input: 'string'
  })
}).then(r => r.json()).then(console.log);`;

    const responseExample = `{
  "workflow_id": "${workflow.id}",
  "output_node_type": "text",
  "is_error": boolean,
  "response": {
    "type": "text",
    "content": "string"
  }
}`;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle1">Endpoint</Typography>
            <Box sx={{ fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
                {endpoint}
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>cURL Example</Typography>
            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
                {curlExample}
            </Box>

            <Typography variant="subtitle1">JavaScript Example</Typography>
            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
                {fetchExample}
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>Response Format</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Workflows return a JSON response with the following format:
            </Typography>
            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
                {responseExample}
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>Request Parameters</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>user_input</strong> (required): The message or input to send to the workflow
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>Response Fields</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>workflow_id</strong>: The ID of the executed workflow
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>output_node_type</strong>: The type of output node (e.g., "text")
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>is_error</strong>: Boolean indicating if an error occurred during execution
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>response</strong>: Object containing the actual output with type and content
            </Typography>
        </Box>
    );
};

export default WorkflowPublicApi;
